import { startTransition, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { DatePicker } from '@mantine/dates'
import { ActionIcon, Group, MantineStyleProps, Stack, Text, UnstyledButton } from '@mantine/core'
import { TbChevronLeft, TbChevronRight } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'

type RangePreset = {
  value: [string, string]
  label: string
}

const formatYmd = (date: Date | null): string | null => (date ? dayjs(date).format('YYYY-MM-DD') : null)

// Parse a 'YYYY-MM-DD' string as a local-midnight Date so the value we hand back
// to callers does not silently shift across the UTC boundary.
const parseYmd = (value: string): Date => dayjs(value, 'YYYY-MM-DD').toDate()

export function DateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onRangeChange,
  onRangeSelectionStateChange,
  mb = 'md',
  presets,
}: {
  startDate: Date | null
  endDate: Date | null
  onStartDateChange: (date: Date | null) => void
  onEndDateChange: (date: Date | null) => void
  onRangeChange?: (startDate: Date | null, endDate: Date | null) => void
  onRangeSelectionStateChange?: (isRangeSelectionInProgress: boolean) => void
  mb?: MantineStyleProps['mb']
  presets?: RangePreset[]
}) {
  const { t, i18n } = useTranslation('common')
  // `i18n.language` is read so the formatted range label re-renders when the
  // user switches UI language (dayjs's locale is set globally in `src/i18n.ts`).
  void i18n.language
  const resolvedPresets: RangePreset[] = presets ?? [
    {
      value: [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      label: t('today'),
    },
    {
      value: [dayjs().subtract(2, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      label: t('lastTwoDays'),
    },
    {
      value: [dayjs().subtract(7, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      label: t('lastSevenDays'),
    },
    {
      value: [dayjs().subtract(14, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      label: t('lastFourteenDays'),
    },
    {
      value: [dayjs().subtract(30, 'day').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
      label: t('lastThirtyDays'),
    },
  ]

  // Stabilise the prop dates by their YMD string form. The parent typically
  // recreates `Date` objects on every render (e.g. via `parseAndValidateDates`),
  // so depending on the raw Date references would make the sync effect fire on
  // every parent re-render — overwriting in-progress range selection.
  const startKey = formatYmd(startDate)
  const endKey = formatYmd(endDate)

  // Controlled value held internally so partial range selection (first click only)
  // can render in the calendar without the parent having to model the in-progress state.
  const [internalValue, setInternalValue] = useState<[string | null, string | null]>([startKey, endKey])
  const [displayedDate, setDisplayedDate] = useState<string>(endKey ?? dayjs().format('YYYY-MM-DD'))

  // Sync internal value only when the parent's startDate/endDate actually change
  // (e.g. URL params updating after navigation), not on every re-render.
  useEffect(() => {
    setInternalValue([startKey, endKey])
  }, [startKey, endKey])

  const propagate = (value: [string | null, string | null]) => {
    const [s, e] = value
    const isRangeSelectionInProgress = Boolean((s && !e) || (!s && e))
    onRangeSelectionStateChange?.(isRangeSelectionInProgress)

    // Wrap the parent callbacks (which typically trigger URL navigation and a
    // suspending query) in a transition so React keeps the previous page UI
    // visible while the new data loads, instead of swapping to the Suspense
    // fallback and shifting layout on every arrow press / preset click.
    startTransition(() => {
      if (s && e) {
        const d1 = parseYmd(s)
        const d2 = parseYmd(e)
        if (onRangeChange) {
          // Ensure logical chronological order when handing off the full range.
          const start = dayjs(d1).isBefore(d2) ? d1 : d2
          const end = dayjs(d1).isBefore(d2) ? d2 : d1
          onRangeChange(start, end)
        } else {
          // Manual selection without a range handler: preserve click order.
          onStartDateChange(d1)
          onEndDateChange(d2)
        }
      } else if (!onRangeChange) {
        onStartDateChange(s ? parseYmd(s) : null)
        onEndDateChange(e ? parseYmd(e) : null)
      }
    })
  }

  const handleDateRangeChange = (value: [string | null, string | null]) => {
    setInternalValue(value)
    propagate(value)
  }

  const activePresetIndex = resolvedPresets.findIndex(
    p => p.value[0] === internalValue[0] && p.value[1] === internalValue[1]
  )

  const handlePresetClick = (preset: RangePreset) => {
    setInternalValue(preset.value)
    setDisplayedDate(preset.value[0])
    propagate(preset.value)
  }

  // Step the selected range by its own duration: a single day steps 1 day,
  // a 7-day range steps 7 days, an N-day range steps N days. Both endpoints
  // shift together so the range length is preserved. Disabled while a range
  // is being selected (only the first click has happened).
  const [start, end] = internalValue
  const hasFullRange = Boolean(start && end)
  const rangeLengthDays = hasFullRange ? dayjs(end!).startOf('day').diff(dayjs(start!).startOf('day'), 'day') + 1 : 0

  const shiftRange = (direction: 1 | -1) => {
    if (!hasFullRange) {
      return
    }
    const step = rangeLengthDays * direction
    const nextStart = dayjs(start!).add(step, 'day').format('YYYY-MM-DD')
    const nextEnd = dayjs(end!).add(step, 'day').format('YYYY-MM-DD')
    const nextValue: [string, string] = [nextStart, nextEnd]
    setInternalValue(nextValue)
    setDisplayedDate(nextStart)
    propagate(nextValue)
  }

  // Compact range label: drop the parts that repeat between the two endpoints.
  //   single day:                       16 May 2026
  //   same month & year:                16 – 17 May 2026
  //   different month, same year:       30 Apr – 2 May 2026
  //   different year:                   30 Dec 2025 – 2 Jan 2026
  const rangeLabel = (() => {
    if (!hasFullRange) {
      return null
    }
    const s = dayjs(start!)
    const e = dayjs(end!)
    if (start === end) {
      return s.format('D MMM YYYY')
    }
    const sameYear = s.isSame(e, 'year')
    const sameMonth = sameYear && s.isSame(e, 'month')
    if (sameMonth) {
      return `${s.format('D')} – ${e.format('D MMM YYYY')}`
    }
    if (sameYear) {
      return `${s.format('D MMM')} – ${e.format('D MMM YYYY')}`
    }
    return `${s.format('D MMM YYYY')} – ${e.format('D MMM YYYY')}`
  })()

  return (
    <Group align="flex-start" gap="md" mb={mb} wrap="nowrap">
      <Stack gap={4} pr="sm" style={{ borderInlineEnd: '1px solid var(--mantine-color-default-border)' }}>
        {resolvedPresets.map((preset, index) => (
          <UnstyledButton
            key={preset.label}
            className="date-range-preset-button"
            data-active={index === activePresetIndex || undefined}
            onMouseDown={e => e.preventDefault()}
            onClick={() => handlePresetClick(preset)}
          >
            {preset.label}
          </UnstyledButton>
        ))}
      </Stack>
      <Stack gap="xs">
        <Group gap="xs" justify="center" align="center" wrap="nowrap">
          <ActionIcon
            variant="default"
            size="lg"
            onClick={() => shiftRange(-1)}
            disabled={!hasFullRange}
            aria-label={t('previousInterval')}
          >
            <TbChevronLeft size={18} />
          </ActionIcon>
          <Text size="sm" fw={500} ta="center" miw={200}>
            {rangeLabel ?? '—'}
          </Text>
          <ActionIcon
            variant="default"
            size="lg"
            onClick={() => shiftRange(1)}
            disabled={!hasFullRange}
            aria-label={t('nextInterval')}
          >
            <TbChevronRight size={18} />
          </ActionIcon>
        </Group>
        <DatePicker
          headerControlsOrder={['level', 'previous', 'next']}
          type="range"
          value={internalValue}
          onChange={handleDateRangeChange}
          date={displayedDate}
          onDateChange={setDisplayedDate}
          size="md"
          allowSingleDateInRange
          styles={{
            calendarHeaderLevel: {
              justifyContent: 'flex-start',
              paddingInlineStart: 8,
            },
          }}
        />
      </Stack>
    </Group>
  )
}
